import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import {
  type BusinessOperationTraceV1,
  BusinessOperationTraceV1Schema,
  isBusinessOperationTraceV1,
} from "./business-operation.js";
import { type PurchaseOrderNumber, PurchaseOrderNumberSchema } from "./purchase-order-number.js";
import {
  PurchasePayableProjectionStatusSchema,
  type SupplierPaymentMethod,
  SupplierPaymentMethodSchema,
} from "./purchase-payable.js";
import {
  deriveSupplierPaymentNumber,
  isBusinessNumberSourceId,
  type SupplierPaymentNumber,
  SupplierPaymentNumberSchema,
} from "./purchase-payable-number.js";
import {
  type PurchaseReceiptNumber,
  PurchaseReceiptNumberSchema,
} from "./purchase-receipt-number.js";

const SafeAmountFenSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const PositiveAmountFenSchema = Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER });
const VersionSchema = Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER });
const StableIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]*$",
});
const BusinessDateSchema = Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" });
const TimestampSchema = Type.String({
  pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}(?:\\.[0-9]{3})?Z$",
});
const CanonicalTextSchema = (maxLength: number) => Type.String({ minLength: 1, maxLength });
const Sha256Schema = Type.String({ pattern: "^[a-f0-9]{64}$" });

export const PreviewSupplierBatchPaymentRequestSchema = Type.Object(
  {
    supplierId: StableIdSchema,
    businessDate: BusinessDateSchema,
    amountFen: PositiveAmountFenSchema,
    method: SupplierPaymentMethodSchema,
    externalReference: Type.Union([CanonicalTextSchema(80), Type.Null()]),
    note: Type.Union([CanonicalTextSchema(200), Type.Null()]),
  },
  { additionalProperties: false },
);
export type PreviewSupplierBatchPaymentRequest = Static<
  typeof PreviewSupplierBatchPaymentRequestSchema
>;

export const SupplierBatchPaymentAllocationSchema = Type.Object(
  {
    allocationOrder: Type.Integer({ minimum: 1, maximum: 100 }),
    receiptId: StableIdSchema,
    receiptNumber: PurchaseReceiptNumberSchema,
    receiptBusinessDate: BusinessDateSchema,
    purchaseOrderId: StableIdSchema,
    purchaseOrderNumber: PurchaseOrderNumberSchema,
    reconciliationId: StableIdSchema,
    reconciliationBusinessDate: BusinessDateSchema,
    allocatedFen: PositiveAmountFenSchema,
    payableOutstandingFenBefore: PositiveAmountFenSchema,
    payableOutstandingFenAfter: SafeAmountFenSchema,
    payableBalanceVersionBefore: VersionSchema,
    payableBalanceVersionAfter: VersionSchema,
    causationId: StableIdSchema,
  },
  { additionalProperties: false },
);
export type SupplierBatchPaymentAllocation = Static<typeof SupplierBatchPaymentAllocationSchema>;

export const SupplierBatchPaymentPreviewSchema = Type.Object(
  {
    kind: Type.Literal("SUPPLIER_BATCH_PAYMENT_PREVIEW"),
    supplierId: StableIdSchema,
    supplierName: CanonicalTextSchema(80),
    businessDate: BusinessDateSchema,
    amountFen: PositiveAmountFenSchema,
    method: SupplierPaymentMethodSchema,
    externalReference: Type.Union([CanonicalTextSchema(80), Type.Null()]),
    note: Type.Union([CanonicalTextSchema(200), Type.Null()]),
    allocations: Type.Array(SupplierBatchPaymentAllocationSchema, {
      minItems: 1,
      maxItems: 100,
    }),
    availableOutstandingFen: PositiveAmountFenSchema,
    sourceReferenceSha256: Sha256Schema,
    businessFactSha256: Sha256Schema,
    canSubmit: Type.Boolean(),
    blocker: Type.Union([CanonicalTextSchema(300), Type.Null()]),
    generatedAt: TimestampSchema,
  },
  { additionalProperties: false },
);
export type SupplierBatchPaymentPreview = Static<typeof SupplierBatchPaymentPreviewSchema>;

export const RecordSupplierBatchPaymentRequestSchema = Type.Object(
  {
    paymentId: StableIdSchema,
    supplierId: StableIdSchema,
    businessDate: BusinessDateSchema,
    amountFen: PositiveAmountFenSchema,
    method: SupplierPaymentMethodSchema,
    externalReference: Type.Union([CanonicalTextSchema(80), Type.Null()]),
    note: Type.Union([CanonicalTextSchema(200), Type.Null()]),
    sourceReferenceSha256: Sha256Schema,
    businessFactSha256: Sha256Schema,
    operationTrace: BusinessOperationTraceV1Schema,
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type RecordSupplierBatchPaymentRequest = Static<
  typeof RecordSupplierBatchPaymentRequestSchema
>;

export const SupplierBatchPaymentRecordSchema = Type.Object(
  {
    paymentId: StableIdSchema,
    paymentNumber: SupplierPaymentNumberSchema,
    supplierId: StableIdSchema,
    supplierName: CanonicalTextSchema(80),
    businessDate: BusinessDateSchema,
    amountFen: PositiveAmountFenSchema,
    method: SupplierPaymentMethodSchema,
    externalReference: Type.Union([CanonicalTextSchema(80), Type.Null()]),
    note: Type.Union([CanonicalTextSchema(200), Type.Null()]),
    sourceReferenceSha256: Sha256Schema,
    businessFactSha256: Sha256Schema,
    allocations: Type.Array(SupplierBatchPaymentAllocationSchema, {
      minItems: 2,
      maxItems: 100,
    }),
    payerActorId: StableIdSchema,
    operationTrace: BusinessOperationTraceV1Schema,
    recordedAt: TimestampSchema,
    evidence: Type.Literal("STORE_ACCEPTED"),
  },
  { additionalProperties: false },
);
export type SupplierBatchPaymentRecord = Static<typeof SupplierBatchPaymentRecordSchema>;

export type SupplierBatchPaymentHashScope = {
  tenantId: string;
  storeId: string;
  actorId: string;
};

type SupplierBatchPaymentHashFacts = Pick<
  SupplierBatchPaymentRecord,
  | "supplierId"
  | "supplierName"
  | "businessDate"
  | "amountFen"
  | "method"
  | "externalReference"
  | "note"
> & {
  readonly allocations: readonly SupplierBatchPaymentAllocation[];
};

type CanonicalSupplierBatchPaymentBusinessFact = Omit<
  SupplierBatchPaymentHashFacts,
  "allocations"
> & {
  allocations: SupplierBatchPaymentAllocation[];
};

type SupplierBatchPaymentSourceHashFacts = SupplierBatchPaymentHashFacts & {
  businessFactSha256: string;
};

export function canonicalSupplierBatchPaymentJson(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(canonicalSupplierBatchPaymentJson).join(",")}]`;
  }
  if (value && typeof value === "object") {
    return `{${Object.entries(value)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, child]) => `${JSON.stringify(key)}:${canonicalSupplierBatchPaymentJson(child)}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
}

export function supplierBatchPaymentBusinessFactHashInput(
  value: SupplierBatchPaymentHashFacts,
): CanonicalSupplierBatchPaymentBusinessFact {
  return {
    supplierId: value.supplierId,
    supplierName: value.supplierName,
    businessDate: value.businessDate,
    amountFen: value.amountFen,
    method: value.method,
    externalReference: value.externalReference,
    note: value.note,
    allocations: [...value.allocations],
  };
}

export function supplierBatchPaymentSourceReferenceHashInput(
  scope: SupplierBatchPaymentHashScope,
  value: SupplierBatchPaymentSourceHashFacts,
): unknown {
  return {
    tenantId: scope.tenantId,
    storeId: scope.storeId,
    actorId: scope.actorId,
    request: {
      supplierId: value.supplierId,
      businessDate: value.businessDate,
      amountFen: value.amountFen,
      method: value.method,
      externalReference: value.externalReference,
      note: value.note,
    },
    businessFactSha256: value.businessFactSha256,
    allocationSources: value.allocations.map((allocation) => ({
      receiptId: allocation.receiptId,
      reconciliationId: allocation.reconciliationId,
      outstandingFen: allocation.payableOutstandingFenBefore,
      version: allocation.payableBalanceVersionBefore,
      causationId: allocation.causationId,
    })),
  };
}

export const SupplierBatchPaymentWriteResultSchema = Type.Object(
  {
    payment: SupplierBatchPaymentRecordSchema,
    cloudProjectionStatus: PurchasePayableProjectionStatusSchema,
  },
  { additionalProperties: false },
);
export type SupplierBatchPaymentWriteResult = Static<typeof SupplierBatchPaymentWriteResultSchema>;

export const SupplierBatchPaymentBusinessEventPayloadSchema = Type.Object(
  { payment: SupplierBatchPaymentRecordSchema },
  { additionalProperties: false },
);

function isBusinessDate(value: string): boolean {
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) &&
    new Date(milliseconds).toISOString() === `${value}T00:00:00.000Z`
  );
}

function isTimestamp(value: string): boolean {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

function isCanonicalNullableText(value: string | null): boolean {
  return value === null || value === value.trim();
}

function allocationsAreExact(
  allocations: readonly SupplierBatchPaymentAllocation[],
  expectedAmountFen: number,
): boolean {
  let sum = 0;
  let previousSortKey: string | null = null;
  const receiptIds = new Set<string>();
  const reconciliationIds = new Set<string>();
  for (const [index, allocation] of allocations.entries()) {
    if (
      allocation.allocationOrder !== index + 1 ||
      receiptIds.has(allocation.receiptId) ||
      reconciliationIds.has(allocation.reconciliationId) ||
      !isBusinessDate(allocation.receiptBusinessDate) ||
      !isBusinessDate(allocation.reconciliationBusinessDate) ||
      allocation.reconciliationBusinessDate < allocation.receiptBusinessDate ||
      allocation.allocatedFen > allocation.payableOutstandingFenBefore ||
      allocation.payableOutstandingFenAfter !==
        allocation.payableOutstandingFenBefore - allocation.allocatedFen ||
      allocation.payableBalanceVersionAfter !== allocation.payableBalanceVersionBefore + 1 ||
      (index < allocations.length - 1 &&
        allocation.allocatedFen !== allocation.payableOutstandingFenBefore)
    ) {
      return false;
    }
    const sortKey = `${allocation.reconciliationBusinessDate}\0${allocation.receiptNumber}\0${allocation.receiptId}`;
    if (previousSortKey !== null && sortKey <= previousSortKey) return false;
    previousSortKey = sortKey;
    receiptIds.add(allocation.receiptId);
    reconciliationIds.add(allocation.reconciliationId);
    sum += allocation.allocatedFen;
    if (!Number.isSafeInteger(sum)) return false;
  }
  return sum === expectedAmountFen;
}

export function isPreviewSupplierBatchPaymentRequest(
  value: unknown,
): value is PreviewSupplierBatchPaymentRequest {
  if (!Value.Check(PreviewSupplierBatchPaymentRequestSchema, value)) return false;
  return (
    isBusinessDate(value.businessDate) &&
    (value.method === "CASH" || value.externalReference !== null) &&
    isCanonicalNullableText(value.externalReference) &&
    isCanonicalNullableText(value.note)
  );
}

export function isSupplierBatchPaymentPreview(
  value: unknown,
): value is SupplierBatchPaymentPreview {
  if (!Value.Check(SupplierBatchPaymentPreviewSchema, value)) return false;
  return (
    isBusinessDate(value.businessDate) &&
    isTimestamp(value.generatedAt) &&
    (value.method === "CASH" || value.externalReference !== null) &&
    isCanonicalNullableText(value.externalReference) &&
    isCanonicalNullableText(value.note) &&
    allocationsAreExact(value.allocations, value.amountFen) &&
    value.allocations.every(
      (allocation) => allocation.reconciliationBusinessDate <= value.businessDate,
    ) &&
    value.availableOutstandingFen >= value.amountFen &&
    value.canSubmit === (value.blocker === null) &&
    (!value.canSubmit || value.allocations.length >= 2)
  );
}

export function isRecordSupplierBatchPaymentRequest(
  value: unknown,
): value is RecordSupplierBatchPaymentRequest {
  if (!Value.Check(RecordSupplierBatchPaymentRequestSchema, value)) return false;
  const trace = value.operationTrace as BusinessOperationTraceV1;
  return (
    isBusinessNumberSourceId(value.paymentId) &&
    isBusinessDate(value.businessDate) &&
    (value.method === "CASH" || value.externalReference !== null) &&
    isCanonicalNullableText(value.externalReference) &&
    isCanonicalNullableText(value.note) &&
    isBusinessOperationTraceV1(trace) &&
    trace.commandId === value.idempotencyKey &&
    trace.idempotencyKey === value.idempotencyKey &&
    trace.correlationId === `supplier-batch-payment:${value.supplierId}` &&
    trace.causationId === null &&
    trace.businessNumber === null
  );
}

export function isSupplierBatchPaymentRecord(value: unknown): value is SupplierBatchPaymentRecord {
  if (!Value.Check(SupplierBatchPaymentRecordSchema, value)) return false;
  return (
    isBusinessNumberSourceId(value.paymentId) &&
    isBusinessDate(value.businessDate) &&
    isTimestamp(value.recordedAt) &&
    value.paymentNumber === deriveSupplierPaymentNumber(value.businessDate, value.paymentId) &&
    (value.method === "CASH" || value.externalReference !== null) &&
    isCanonicalNullableText(value.externalReference) &&
    isCanonicalNullableText(value.note) &&
    allocationsAreExact(value.allocations, value.amountFen) &&
    value.allocations.every(
      (allocation) => allocation.reconciliationBusinessDate <= value.businessDate,
    ) &&
    isBusinessOperationTraceV1(value.operationTrace) &&
    value.operationTrace.commandId === value.operationTrace.idempotencyKey &&
    value.operationTrace.correlationId === `supplier-batch-payment:${value.supplierId}` &&
    value.operationTrace.causationId === null &&
    value.operationTrace.businessNumber === null
  );
}

export type SupplierBatchPaymentAllocationSnapshot = {
  receiptId: string;
  receiptNumber: PurchaseReceiptNumber;
  purchaseOrderNumber: PurchaseOrderNumber;
  payableOutstandingFenBefore: number;
  payableOutstandingFenAfter: number;
};

export type SupplierBatchPaymentIdentity = {
  paymentNumber: SupplierPaymentNumber;
  supplierId: string;
  method: SupplierPaymentMethod;
};
